import React, { Component } from 'react';
import PropTypes from 'prop-types';
import utils from '../utils/index';
import TenCascaderMenu from './cascader-menu';
import TreeModel from '../tree/tree-model';
import isEqual from 'lodash/isEqual';
import { getLabelPathStrByModel, addChild } from './cascader-utils';

export default class CascaderPanel extends Component {
  static propTypes = {
    /**
     * v-model 当前值，对应事件 change
     */
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
    /**
     * 非受控时，可传入默认值
     */
    defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.object, PropTypes.array]),
    /**
     * 筛选结果分割符
     */
    separator: PropTypes.string,
    /** r
     * 选项数据源
     */
    options: PropTypes.array,
    /**
     * @ignore
     */
    onOptionsUpdate: PropTypes.func,
    /**
     * 选项 title，自定义每个选项列表的标题
     */
    optionsTitle: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
    /**
     * 是否多选
     */
    multiple: PropTypes.bool,
    /**
     * 每个选项都独立可选，而非只有叶子节点可选
     */
    independent: PropTypes.bool,
    /**
     * 指定选项的 value 在 data 中的键值
     */
    optionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 指定选项的 label 在 data 中的键值
     */
    optionLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 指定选项的 children 在 data 中的键值
     */
    optionChildren: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    /**
     * 自定义渲染 option 函数，不适用于筛选节点
     */
    renderOption: PropTypes.func,
    /**
     * 自定义加载子节点方法，需设置需要动态加载的节点 children 为 true
     * (data, dataPath) => Promise
     */
    loadChildren: PropTypes.func,
    /**
     * 是否根据选项自动加载子节点（比如初始化 value 有值的时候）
     */
    autoLoadChildren: PropTypes.bool,
    /**
     * 子菜单的展开方式
     * @member click | hover
     */
    expandTrigger: PropTypes.string,
    /**
     * @ignore
     */
    filterText: PropTypes.string,
    /**
     * @ignore
     */
    filterOption: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    /**
     * 仅子节点可选（一般在 multiple 模式下使用）
     */
    onlyLeafSelectable: PropTypes.bool,
    /**
     * 结果为空时提示文字，支持同名slot
     */
    emptyTip: PropTypes.string,
  };

  static defaultProps = {
    separator: '/',
    optionValue: 'value',
    optionLabel: 'label',
    optionChildren: 'children',
    autoLoadChildren: true,
    expandTrigger: 'click',
    filterText: '',
    emptyTip: '暂无数据',
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const {
      independent,
      filterText,
      filterOption,
      value,
      optionChildren,
      optionValue,
      multiple,
      optionLabel,
      separator,
    } = nextProps;

    const newState = {
      treeModel: prevState.treeModel,
      selectMode: independent ? 'independent' : 'default',
      selectedValueMode: independent ? 'all' : 'onlyLeaf',
      filterText: filterText.trim(),
      showFilter: !!(filterOption && filterText.trim()),
    };

    /* transform selected */
    const transform = (v) => {
      let transformedValue;
      if (!multiple) {
        transformedValue = !utils.isEmpty(v) ? [v] : [];
      } else {
        transformedValue = Array.isArray(v) ? v : [];
      }
      transformedValue = transformedValue
        .map((item) => {
          if (Array.isArray(item) && item.length > 0) {
            return item;
          }
          return undefined;
        })
        .filter((item) => item);
      return transformedValue;
    };

    newState.dataValue = transform(value);

    /* remake treeModel */
    if (nextProps.options !== prevState.options) {
      newState.options = nextProps.options;
      newState.treeModel = new TreeModel({
        selectMode: independent ? 'independent' : 'default',
      });
      newState.treeModel.buildFromArray(
        prevState.options,
        (item) => item[optionValue],
        (item) => item[optionChildren],
      );
      newState.treeModel.setSelected(newState.dataValue);
      newState.treeModel.applySelected();
    }

    const getFilteredOptions = (text) => {
      const filteredOptions = [];
      const defaultFilterFunc = (node) => {
        const labelPath = getLabelPathStrByModel(node, optionLabel, separator);
        return labelPath.indexOf(text) > -1;
      };
      if (newState.treeModel) {
        newState.treeModel.walk((node) => {
          if (!independent && !node.isLeaf) {
            return;
          }
          const { data } = node;
          if (typeof filterOption === 'function' && filterOption(data)) {
            filteredOptions.push(node);
          } else if (defaultFilterFunc(node)) {
            filteredOptions.push(node);
          }
        });
      }

      return filteredOptions;
    };

    if (
      nextProps.options !== prevState.options ||
      nextProps.filterData !== prevState.filterData ||
      nextProps.filterText !== prevState.filterText
    ) {
      newState.filteredOptions = getFilteredOptions(nextProps.filterText.trim());
      //   newState.filteredNodeModels = filteredNodeModels;
    }
    //
    newState.treeModel.setExpanded(newState.expanded);
    newState.treeModel.applyExpanded();
    //
    newState.treeModel.setSelected(newState.dataValue);
    newState.treeModel.applySelected();

    return newState;
  }

  static childContextTypes = {
    tenCascaderPanel: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      popupShow: false,
      valueDataMap: {},
      treeModel: null,
      expandedNodes: [],
      expandedPath: [],
      filteredOptions: [],
      loadErrorPaths: [],
      dataValue: [],
      selectMode: '',
      selectedValueMode: '',
      filterText: '',
      showFilter: false,
      inited: false,
      cloneData: [],
      options: [],
    };
    this.refMenuElem = null;
  }

  getChildContext() {
    return {
      tenCascaderPanel: this,
    };
  }

  componentDidMount() {
    const { dataValue } = this.state;
    const { loadChildren, autoLoadChildren } = this.props;
    if (dataValue.length > 0) {
      const [expandedPath] = dataValue.slice(-1);

      this.setState({
        expandedPath,
      });
    }

    this.initTreeModel();

    if (loadChildren && autoLoadChildren) {
      this.loadPathsOptions().then(() => {
        if (!this.state.inited) {
          this.setState({
            inited: true,
          });
          this.initTreeModel();
        }
      });
    } else {
      this.setState({
        inited: true,
      });
    }
  }
  componentDidUpdate(prevProps, prevState) {
    const { loadChildren, autoLoadChildren } = this.props;
    if (!isEqual(prevState.dataValue, this.state.dataValue)) {
      const { dataValue, treeModel } = this.state;

      if (loadChildren && autoLoadChildren) {
        if (dataValue.length > 0) {
          const [expandedPath] = dataValue.slice(-1);
          this.setState({ expandedPath });
        }
        this.loadPathsOptions().then(() => {
          treeModel.setSelected(dataValue);
          treeModel.applySelected();
        });
      } else {
        if (dataValue.length > 0) {
          const [expandedPath] = dataValue.slice(-1);
          this.setState({ expandedPath });
          this.updateExpandedNodes();
        }
        treeModel.setSelected(dataValue);
        treeModel.applySelected();
      }
    }
  }

  initTreeModel() {
    const { optionValue, optionChildren, options, independent } = this.props;
    const { dataValue } = this.state;
    const treeModel = new TreeModel();
    const getKey = (data) => data[optionValue];
    const getChildren = (data) => data[optionChildren];

    treeModel.buildFromArray(options, getKey, getChildren);
    treeModel.setConfig({
      selectMode: independent ? 'independent' : 'default',
    });
    treeModel.setSelected(dataValue);
    treeModel.applySelected();
    this.setState(
      {
        treeModel,
      },
      () => {
        this.updateExpandedNodes();
        this.props.onOptionsUpdate && this.props.onOptionsUpdate();
      },
    );
  }

  loadPathsOptions() {
    const { dataValue, loadErrorPaths } = this.state;
    return new Promise((resolve) => {
      if (!this.props.loadChildren) {
        resolve();
        return;
      }

      const paths = dataValue.filter((p) => !loadErrorPaths.find((eP) => isEqual(p, eP)));

      const promises = [];

      for (let i = 0; i < paths.length; i++) {
        promises.push(
          new Promise((pathResolve) => {
            // eslint-disable-line
            this.loadPathOptions(paths[i], pathResolve);
          }),
        );
      }

      setTimeout(() => {
        resolve();
      }, 3000);
      return Promise.all(promises).then(() => {
        resolve();
      });
    });
  }

  loadPathOptions(path, done) {
    const { optionChildren, loadChildren } = this.props;
    const dataPath = this.getDataPathByPath(path);

    if (dataPath.length > 0) {
      const lastData = dataPath[dataPath.length - 1];
      const children = lastData[optionChildren];

      if (children && !Array.isArray(children)) {
        const p = loadChildren(lastData, dataPath);

        if (typeof p === 'object' && p.then) {
          p.then(
            (result) => {
              lastData[optionChildren] = result;
              this.loadPathOptions(path, done);
            },
            () => {
              this.setState({ loadErrorPaths: this.state.loadErrorPaths.push(path) });
              done(1);
            },
          );
        } else {
          done(2);
        }
      } else {
        done(3);
      }
    } else {
      done(4);
    }
  }

  getDataPathByPath(path) {
    const dataPath = [];
    const { optionChildren, optionValue, options } = this.props;
    let children = options;

    for (let i = 0; i < path.length && Array.isArray(children); i++) {
      const key = path[i];
      const foundData = children.find((child) => child[optionValue] === key);

      if (foundData) {
        dataPath.push(foundData);
        children = foundData[optionChildren];
      }
    }

    return dataPath;
  }

  getMenuTitle = (model, index) => {
    const { optionChildren, optionsTitle } = this.props;

    if (Array.isArray(optionsTitle) && optionsTitle[index]) {
      return optionsTitle[index];
    }

    if (typeof optionsTitle === 'function') {
      const { data } = model;
      const options = index === 0 ? options : data[optionChildren];
      /**
       * optionsTitle
       */

      return optionsTitle(index, options, data);
    }

    return '';
  };

  focus() {
    if (this.refMenuElem && this.refMenuElem[0]) {
      this.refMenuElem[0].focus(-1);
    }
  }

  blur() {
    if (this.refMenuElem) {
      this.refMenuElem.forEach((menu) => menu.blur());
    }
  }

  reverseValue(selectedPaths) {
    const { multiple } = this.props;
    let reversedValue = selectedPaths;

    if (!multiple) {
      reversedValue = reversedValue.length > 0 ? reversedValue[0] : null;
    }

    return reversedValue;
  }

  updateExpandedNodes(model) {
    let triggerNode = model;

    if (!triggerNode) {
      triggerNode = this.state.treeModel.getNodeByPath(this.state.expandedPath);
    }

    if (!triggerNode) {
      this.setState({
        expandedNodes: [this.state.treeModel],
      });
      return;
    }

    const nodePath = triggerNode.getNodePath();
    const expandedNodes = nodePath;

    for (let i = 0; i < expandedNodes.length; i++) {
      const node = expandedNodes[i];

      if (node.isLeaf || node.data.disabled) {
        expandedNodes.length = i;
      }
    }

    expandedNodes.unshift(this.state.treeModel);
    this.setState({
      expandedPath: triggerNode.path,
      expandedNodes,
    });
  }

  loadNodeChildren(nodeModel) {
    if (nodeModel.data[this.optionsChildren]) {
    }
  }

  onNodeExpand(treeNodeModel) {
    this.updateExpandedNodes(treeNodeModel);
    const rawData = treeNodeModel.data;
    const { optionChildren, loadChildren, optionValue } = this.props;
    if (
      this.props.loadChildren &&
      !treeNodeModel.isLeaf &&
      !Array.isArray(rawData[optionChildren]) &&
      !treeNodeModel.loading
    ) {
      treeNodeModel.loading = true;
      const nodePath = treeNodeModel.getNodePath();
      const dataPath = nodePath.map((node) => node.data);
      const rawData = treeNodeModel.data;
      const p = loadChildren(rawData, dataPath);

      if (typeof p === 'object' && typeof p.then === 'function') {
        p.then(
          (childrenData) => {
            treeNodeModel.loading = false;
            rawData[optionChildren] = childrenData;
            addChild(treeNodeModel, childrenData, optionValue, optionChildren);
            this.updateExpandedNodes();
            this.forceUpdate();
          },
          () => {
            treeNodeModel.loading = false;
            this.updateExpandedNodes();
            this.forceUpdate();
          },
        );
      } else {
        treeNodeModel.loading = false;
        this.forceUpdate();
      }
    }
  }

  onNodeSelect(treeNodeModel, selected) {
    const { independent, onChange, multiple } = this.props;
    const { path } = treeNodeModel;
    let selectedPaths;
    if (!onChange) {
      return;
    }

    if (!multiple) {
      selectedPaths = [path];
    } else {
      this.state.treeModel.handleSelectedChange(path, selected);
      selectedPaths = this.state.treeModel.getSelectedPaths(independent ? 'all' : 'onlyLeaf');
    }
    this.setState({ treeModel: this.state.treeModel });
    const reversedValue = this.reverseValue(selectedPaths);
    onChange(reversedValue);
  }

  onMenuBlur(triggerMenuIndex, dir) {
    const triggerMenu = this.refMenuElem[triggerMenuIndex];
    let index = this.refMenuElem.indexOf(triggerMenu);

    if (index > -1) {
      index += dir === 'left' ? -1 : 1;

      if (this.refMenuElem[index]) {
        triggerMenu.blur();
        this.refMenuElem[index].focus();
      }
    }
  }

  renderEmptyTip() {
    const { emptyTip } = this.props;
    return <span>{emptyTip}</span>;
  }

  render() {
    const { expandedNodes, showFilter, filteredOptions } = this.state;
    const { optionLabel, separator, renderOption } = this.props;
    return (
      <div>
        {!showFilter ? (
          <div className="ten-cascader-panel">
            {expandedNodes.map((model, index) => (
              <TenCascaderMenu
                ref={(el) => {
                  this.refMenuElem = el;
                }}
                options={model.children}
                index={index}
                loading={model.loading}
                title={this.getMenuTitle(model, index)}
                key={model.key + '-cascader-menu-' + index}
                menuBlur={(dir) => this.onMenuBlur(index, dir)}
                optionLabel={optionLabel}
                separator={separator}
                showFilter={showFilter}
                renderOption={renderOption}
              />
            ))}
          </div>
        ) : (
          <div className="ten-cascader-panel">
            <TenCascaderMenu options={filteredOptions} optionLabel={optionLabel} />
          </div>
        )}
      </div>
    );
  }
}
